/**
 * Wall-clock time in a named IANA zone, without a date library (M1-09).
 *
 * The dispatch board has to answer two questions the platform's own `Date`
 * cannot: "which instant is midnight on 2026-09-15 in America/Chicago?" and
 * "what does 20:30 UTC read as on that business's clock?". A job is stamped
 * with an instant; a business opens at "08:00", which is a recurring local
 * fact, not an instant. Everything on the board sits between those two.
 *
 * `Date` only ever knows UTC and the *browser's* zone. That is usually the
 * account's zone too — a dispatcher sits in the office — but "usually" is how
 * you ship a board that is quietly an hour wrong for the one franchise that
 * opened across a state line, and the account already carries an explicit
 * `Timezone`. These are ~40 lines against pulling in a date library for it.
 *
 * Built on `Intl.DateTimeFormat`, which has had full IANA zone support in
 * browsers and in Node (so, jsdom) for years. There is no fallback path: a
 * runtime without it would get times silently wrong rather than loudly
 * broken, and there isn't one worth supporting.
 */

/** A calendar date with no time and no zone — what a date picker means. */
export interface CivilDate {
  year: number;
  /** 1-12, not the 0-11 that `Date.getMonth()` returns. */
  month: number;
  day: number;
}

const partsFormatter = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsFormatter.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      // `hourCycle: 'h23'` rather than `hour12: false`: the latter is specified
      // to produce "24" for midnight in some engines, which would parse to an
      // hour that does not exist and shift the whole day by one.
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsFormatter.set(timeZone, formatter);
  }
  return formatter;
}

interface ZonedParts extends CivilDate {
  hour: number;
  minute: number;
  second: number;
}

/** What a clock in `timeZone` reads at `instant`. */
function partsAt(instant: Date, timeZone: string): ZonedParts {
  const found: Record<string, number> = {};
  for (const part of formatterFor(timeZone).formatToParts(instant)) {
    if (part.type !== 'literal') {
      found[part.type] = Number(part.value);
    }
  }
  return {
    year: found.year ?? 0,
    month: found.month ?? 1,
    day: found.day ?? 1,
    hour: found.hour ?? 0,
    minute: found.minute ?? 0,
    second: found.second ?? 0,
  };
}

/** Milliseconds `timeZone` is ahead of UTC at `instant` (negative in the Americas). */
function offsetMsAt(instant: Date, timeZone: string): number {
  const parts = partsAt(instant, timeZone);
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return asIfUtc - instant.getTime();
}

/**
 * The instant at which clocks in `timeZone` read the given wall-clock time.
 *
 * Two passes, and the second is not optional. The first guesses the offset by
 * reading it at the *UTC* instant with those digits — which, for a zone hours
 * behind UTC, is hours before the answer. When a DST transition falls in that
 * gap the guess uses the wrong offset. The measured band for America/Chicago
 * is local 03:00-07:59 on each of the two changeover days: 07:00 and 07:30 are
 * both in it, so this is the opening time of an ordinary business, not a
 * contrived edge case. Re-reading the offset at the candidate answer and
 * re-applying it corrects it, and `timezone.test.ts` pins both directions.
 *
 * Times that do not exist (the skipped hour in spring) resolve to the instant
 * the clock jumps to; times that happen twice (the repeated hour in autumn)
 * resolve to the first. Neither is a case a business's opening time falls into
 * in practice, and both are answers rather than exceptions.
 */
export function zonedTimeToInstant(
  { year, month, day }: CivilDate,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const firstGuess = wallClockAsUtc - offsetMsAt(new Date(wallClockAsUtc), timeZone);
  const corrected = wallClockAsUtc - offsetMsAt(new Date(firstGuess), timeZone);
  return new Date(corrected);
}

/** The calendar date `instant` falls on, on that business's own clock. */
export function zonedCivilDate(instant: Date, timeZone: string): CivilDate {
  const { year, month, day } = partsAt(instant, timeZone);
  return { year, month, day };
}

/**
 * What a wall clock in `timeZone` reads at `instant`, as "HH:mm" — the value
 * an `<input type="time">` holds, so a form can be pre-filled from a stored
 * instant on the business's clock rather than the viewer's (M2-52's visit
 * editor). The inverse of `zonedTimeToInstant` for a given date.
 */
export function zonedWallClock(instant: Date, timeZone: string): string {
  const { hour, minute } = partsAt(instant, timeZone);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Minutes after local midnight that `instant` falls on, on `timeZone`'s wall
 * clock — the dispatch board's slot arithmetic and the move dialog's time
 * picker both start from it. One helper rather than the two copies that
 * each split `zonedWallClock`'s string back apart (found in review, M2-52).
 */
export function zonedMinuteOfDay(instant: Date, timeZone: string): number {
  const { hour, minute } = partsAt(instant, timeZone);
  return hour * 60 + minute;
}

/**
 * Whole calendar days from `from` to `to` (negative when `to` is earlier).
 * Civil arithmetic, so a day the clocks change is still one day.
 */
export function daysBetween(from: CivilDate, to: CivilDate): number {
  return Math.round(
    (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) / 86_400_000,
  );
}

/** The weekday `date` falls on. Zone-free: a calendar date has exactly one. */
export function civilDayOfWeek({ year, month, day }: CivilDate): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** `date` shifted by whole days, rolling over months and years. */
export function addDays({ year, month, day }: CivilDate, days: number): CivilDate {
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/** `YYYY-MM-DD` — what an `<input type="date">` and a URL query both speak. */
export function toIsoDate({ year, month, day }: CivilDate): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Parses `YYYY-MM-DD`. Returns null for anything else, including a real-looking `2026-02-31`. */
export function parseIsoDate(value: string): CivilDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const candidate = new Date(Date.UTC(year, month - 1, day));
  // Rejects the dates that only look valid: `Date.UTC` happily rolls
  // 2026-02-31 forward to March 3rd rather than refusing it.
  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() + 1 !== month ||
    candidate.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/** Today, on that business's clock — which is not always today on the viewer's. */
export function todayInZone(timeZone: string, now: Date = new Date()): CivilDate {
  return zonedCivilDate(now, timeZone);
}

/**
 * Parses a `"HH:mm"` business-hours string into minutes past midnight.
 * Returns null rather than guessing at anything malformed — a board that
 * silently treats a bad "8:00am" as midnight is worse than one that falls
 * back to its default range and says so.
 */
export function parseWallClock(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
}
